import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, ArrowLeft, ArrowRight, Loader, StickyNote } from 'lucide-react';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import { useToastContext } from '../context/ToastContext';
import {
  parseSpreadsheet, autoMapHeaders, buildRows, mappingHasName, findInFilePhoneDuplicates,
  MAPPABLE_FIELDS, FIELD_NOTE, FIELD_IGNORE, FIELD_FULL_NAME,
} from '../utils/importParse';
import EnterpriseModal from './EnterpriseModal';
import api from '../services/api';

const PREVIEW_FIELDS = ['firstName', 'middleName', 'lastName', 'phone', 'parentPhone'];

/**
 * Generic, mapping-driven student importer (CSV / XLSX).
 *
 * Unlike the legacy fixed-column CSV importer, this lets the user map each
 * spreadsheet column to a system field. Columns with no matching field default
 * to "Add to notes" — they're stored as student notes (proposals) so nothing is
 * lost and the data stays filterable. A single "full name" column is split into
 * first / middle / last automatically.
 */
export default function StudentImportModal({ isOpen, onClose, onImportComplete, selectedYear }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { success, error: showError } = useToastContext();

  const [step, setStep] = useState('upload');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setStep('upload');
    setHeaders([]);
    setDataRows([]);
    setMapping([]);
    setFileName('');
    setParseError('');
    setIsDragOver(false);
    setIsSubmitting(false);
    setImportResult(null);
    setOnDuplicate('skip');
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const fieldLabel = useCallback((key) => {
    const map = {
      [FIELD_FULL_NAME]: t('students.importMap.fullName', 'Full name'),
      firstName: t('students.importMap.firstName', 'First name'),
      middleName: t('students.importMap.middleName', 'Middle name'),
      lastName: t('students.importMap.lastName', 'Last name'),
      phone: t('students.importMap.phone', 'Phone'),
      parentPhone: t('students.importMap.parentPhone', 'Parent phone'),
      email: t('students.importMap.email', 'Email'),
      nationalId: t('students.importMap.nationalId', 'National ID'),
      gender: t('students.importMap.gender', 'Gender'),
      dateOfBirth: t('students.importMap.dateOfBirth', 'Date of birth'),
      city: t('students.importMap.city', 'City'),
      address: t('students.importMap.address', 'Address'),
      [FIELD_NOTE]: t('students.importMap.note', 'Add to notes'),
      [FIELD_IGNORE]: t('students.importMap.ignore', 'Ignore'),
    };
    return map[key] || key;
  }, [t]);

  const processFile = useCallback(async (file) => {
    setParseError('');
    setFileName(file.name);
    try {
      const { headers: hdrs, dataRows: drows } = await parseSpreadsheet(file);
      setHeaders(hdrs);
      setDataRows(drows);
      setMapping(autoMapHeaders(hdrs));
      setStep('mapping');
    } catch (err) {
      if (err.message === 'UNSUPPORTED_TYPE') {
        setParseError(t('students.importMap.unsupportedType', 'Please select a .csv or .xlsx file'));
      } else if (err.message === 'NO_DATA_ROWS') {
        setParseError(t('students.importMap.noDataRows', 'No data rows found in the file'));
      } else {
        setParseError(t('students.importMap.parseFailed'));
      }
    }
  }, [t]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const built = useMemo(() => {
    if (dataRows.length === 0 || mapping.length === 0) {
      return { rows: [], validCount: 0, invalidCount: 0, maxRowsExceeded: false };
    }
    return buildRows(dataRows, headers, mapping);
  }, [dataRows, headers, mapping]);

  const hasName = useMemo(() => mappingHasName(mapping), [mapping]);
  const validRows = built.rows.filter((r) => r.__valid);
  const notesPerRow = (r) => (r.notes ? r.notes.length : 0);
  const totalNotes = built.rows.reduce((s, r) => s + notesPerRow(r), 0);
  // Indexes into built.rows (not validRows) — only used for preview flagging.
  const inFileDuplicates = useMemo(() => findInFilePhoneDuplicates(
    built.rows.map((r) => (r.__valid ? r : {}))
  ), [built.rows]);

  const handleImport = async () => {
    setIsSubmitting(true);
    try {
      const payload = validRows.map((r) => {
        const { __valid, ...rest } = r;
        return rest;
      });
      const response = await api.post('/students/bulk', {
        rows: payload,
        yearId: selectedYear || null,
        onDuplicate,
      });
      const result = response?.data || response;
      setImportResult(result);
      setStep('result');
      if (result.created > 0 || result.updated > 0) {
        success(t('students.importMap.importSuccess', {
          created: result.created, failed: result.failed,
          defaultValue: `Imported ${result.created} students (${result.failed} failed).`,
        }));
      }
    } catch (err) {
      showError(t('errors.importFailed', 'Import failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const btnBase = {
    padding: '0.5rem 1.25rem', borderRadius: '8px', fontSize: '0.875rem',
    cursor: 'pointer', fontWeight: '500',
  };
  const secondaryBtn = {
    ...btnBase, border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)',
    display: 'flex', alignItems: 'center', gap: '0.375rem',
  };
  const primaryBtn = (enabled = true) => ({
    ...btnBase, border: 'none',
    backgroundColor: enabled ? 'var(--color-primary-dark)' : 'var(--color-border)',
    color: enabled ? 'var(--color-on-primary, #fff)' : 'white', cursor: enabled ? 'pointer' : 'not-allowed',
  });

  const renderFooter = () => {
    if (step === 'upload') {
      return <button onClick={handleClose} style={secondaryBtn}>{t('common.cancel', 'Cancel')}</button>;
    }
    if (step === 'mapping') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <button onClick={reset} style={secondaryBtn}>
            {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}{t('common.back', 'Back')}
          </button>
          <button onClick={() => setStep('preview')} disabled={!hasName || built.validCount === 0} style={primaryBtn(hasName && built.validCount > 0)}>
            {t('students.importMap.continueBtn', 'Continue')} ({built.validCount})
          </button>
        </div>
      );
    }
    if (step === 'preview') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <button onClick={() => setStep('mapping')} style={secondaryBtn}>
            {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}{t('common.back', 'Back')}
          </button>
          <button onClick={handleImport} disabled={isSubmitting || validRows.length === 0} style={{ ...primaryBtn(validRows.length > 0), display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: isSubmitting ? 0.7 : 1 }}>
            {isSubmitting && <Loader size={16} style={{ animation: 'simSpin 1s linear infinite' }} />}
            {isSubmitting ? t('students.importMap.importing', 'Importing...') : `${t('students.importMap.importNow', 'Import')} (${validRows.length})`}
          </button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <button onClick={() => { handleClose(); onImportComplete?.(); }} style={primaryBtn(true)}>
          {t('common.close', 'Close')}
        </button>
      </div>
    );
  };

  const steps = ['upload', 'mapping', 'preview', 'result'];

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      isSubmitting={isSubmitting}
      footer={renderFooter()}
      headerContent={
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: 'var(--color-text-primary)' }}>
              {t('students.importMap.title', 'Import students (CSV / Excel)')}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', padding: '0.75rem 0 0 0', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
            {steps.map((s, i) => (
              <div key={s} style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                color: step === s ? 'var(--color-primary-dark)' : 'var(--color-text-secondary)',
                fontWeight: step === s ? '600' : '400', fontSize: '0.8125rem',
              }}>
                <span style={{
                  width: '22px', height: '22px', borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                  backgroundColor: step === s ? 'var(--color-primary-dark)' : 'var(--color-border)',
                  color: step === s ? 'var(--color-on-primary, #fff)' : 'var(--color-text-secondary)',
                }}>{i + 1}</span>
                {t(`students.importMap.${s}Step`, s.charAt(0).toUpperCase() + s.slice(1))}
              </div>
            ))}
          </div>
        </div>
      }
    >
      <style>{`@keyframes simSpin { to { transform: rotate(360deg); } }`}</style>

      {/* STEP: Upload */}
      {step === 'upload' && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragOver ? 'var(--color-primary-dark)' : 'var(--color-border)'}`,
              borderRadius: '12px', padding: '3rem 2rem', textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.2s',
              backgroundColor: isDragOver ? 'rgba(53, 87, 96, 0.05)' : 'transparent',
            }}
          >
            <Upload size={40} style={{ marginBottom: '0.75rem', color: 'var(--color-text-secondary)', opacity: 0.5 }} />
            <div style={{ fontSize: '0.9375rem', fontWeight: '500', color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>
              {t('students.importMap.dragDrop', 'Drag and drop a CSV or Excel file, or click to select')}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>.csv, .xlsx ({t('students.importMap.maxRows', 'max 500 rows')})</div>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.txt" onChange={handleFileSelect} style={{ display: 'none' }} />
          </div>

          {parseError && (
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: 'var(--color-danger-light, #fef2f2)', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger-dark, #991b1b)', fontSize: '0.8125rem' }}>
              <AlertCircle size={16} />{parseError}
            </div>
          )}

          <div style={{ marginTop: '1.25rem', padding: '0.75rem 1rem', backgroundColor: 'var(--color-background)', borderRadius: '8px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex', gap: '0.5rem' }}>
            <StickyNote size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{t('students.importMap.uploadHint', 'Only a name is required. A single "full name" column is split into first/middle/last. Columns we don\'t recognize are saved as notes you can filter by later.')}</span>
          </div>
        </div>
      )}

      {/* STEP: Mapping */}
      {step === 'mapping' && (
        <div>
          <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={16} color="var(--color-text-secondary)" />
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{fileName}</span>
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            {t('students.importMap.mapHint', 'Map each column to a field. Unmapped columns are added as notes.')}
          </p>

          {!hasName && (
            <div style={{ marginBottom: '1rem', padding: '0.625rem 0.875rem', backgroundColor: 'var(--color-warning-light, #fffbeb)', border: '1px solid #fde68a', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-warning-dark, #92400e)', fontSize: '0.8125rem' }}>
              <AlertCircle size={16} />{t('students.importMap.nameRequired', 'Map at least one column to Full name or First name.')}
            </div>
          )}

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {headers.map((header, idx) => {
              const sample = dataRows.find((r) => String(r[idx] ?? '').trim() !== '')?.[idx] || '';
              return (
                <div key={idx} style={{
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'center',
                  padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: '8px',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {header}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sample ? `${t('students.importMap.sample', 'e.g.')} ${sample}` : '—'}
                    </div>
                  </div>
                  <select
                    value={mapping[idx]}
                    onChange={(e) => setMapping((m) => m.map((v, i) => (i === idx ? e.target.value : v)))}
                    style={{
                      padding: '0.4rem 0.6rem', border: '1px solid var(--color-border)', borderRadius: '6px',
                      fontSize: '0.8125rem', fontFamily: 'inherit', backgroundColor: 'var(--color-surface)',
                      color: 'var(--color-text-primary)', minWidth: '140px',
                    }}
                  >
                    {MAPPABLE_FIELDS.map((f) => (
                      <option key={f} value={f}>{fieldLabel(f)}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP: Preview */}
      {step === 'preview' && (
        <div>
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FileText size={16} color="var(--color-text-secondary)" />
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{fileName}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ padding: '0.25rem 0.5rem', borderRadius: '6px', backgroundColor: 'var(--color-success-light, #ecfdf5)', color: 'var(--color-success-dark, #059669)', fontSize: '0.75rem', fontWeight: '600' }}>
                {validRows.length} {t('students.importMap.validRows', 'valid')}
              </span>
              {built.invalidCount > 0 && (
                <span style={{ padding: '0.25rem 0.5rem', borderRadius: '6px', backgroundColor: 'var(--color-danger-light, #fef2f2)', color: 'var(--color-danger-dark, #dc2626)', fontSize: '0.75rem', fontWeight: '600' }}>
                  {built.invalidCount} {t('students.importMap.invalidRows', 'invalid')}
                </span>
              )}
              {totalNotes > 0 && (
                <span style={{ padding: '0.25rem 0.5rem', borderRadius: '6px', backgroundColor: 'var(--color-info-light, #eff6ff)', color: 'var(--color-info-dark, #2563eb)', fontSize: '0.75rem', fontWeight: '600' }}>
                  {totalNotes} {t('students.importMap.notesLabel', 'notes')}
                </span>
              )}
            </div>
          </div>

          <fieldset style={{ margin: '0 0 1rem', padding: '0.625rem 0.875rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
            <legend style={{ padding: '0 0.25rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--color-text-primary)' }}>
              {t('students.importMap.onDuplicateLabel', 'If the phone number already exists')}
            </legend>
            {['skip', 'update'].map((opt) => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--color-text-primary)', cursor: 'pointer', padding: '0.125rem 0' }}>
                <input type="radio" name="onDuplicate" value={opt} checked={onDuplicate === opt} onChange={() => setOnDuplicate(opt)} />
                {opt === 'skip'
                  ? t('students.importMap.onDuplicateSkip', 'Skip the row (keep the existing student)')
                  : t('students.importMap.onDuplicateUpdate', "Update the existing student with the file's data")}
              </label>
            ))}
          </fieldset>

          <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: '8px', maxHeight: '360px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-background)' }}>
                  <th style={{ padding: '0.5rem', textAlign: isRTL ? 'right' : 'left', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>#</th>
                  {PREVIEW_FIELDS.map((f) => (
                    <th key={f} style={{ padding: '0.5rem', textAlign: isRTL ? 'right' : 'left', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {fieldLabel(f)}{f === 'firstName' ? ' *' : ''}
                    </th>
                  ))}
                  <th style={{ padding: '0.5rem', textAlign: isRTL ? 'right' : 'left', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>{t('students.importMap.note', 'Notes')}</th>
                  <th style={{ padding: '0.5rem', fontWeight: '600', color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>{t('common.status', 'Status')}</th>
                </tr>
              </thead>
              <tbody>
                {built.rows.map((row, i) => (
                  <tr key={i} style={{ backgroundColor: row.__valid ? 'transparent' : 'var(--color-danger-light, #fef2f2)', borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.375rem 0.5rem', color: 'var(--color-text-secondary)' }}>{i + 1}</td>
                    {PREVIEW_FIELDS.map((f) => (
                      <td key={f} style={{ padding: '0.375rem 0.5rem', color: 'var(--color-text-primary)', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row[f] || <span style={{ color: 'var(--color-text-disabled, #cbd5e1)' }}>—</span>}
                      </td>
                    ))}
                    <td style={{ padding: '0.375rem 0.5rem', color: 'var(--color-text-secondary)' }}>
                      {notesPerRow(row) > 0 ? (
                        <span title={row.notes.map((n) => `${n.title}: ${n.content}`).join('\n')} style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                          <StickyNote size={13} />{notesPerRow(row)}
                        </span>
                      ) : <span style={{ color: 'var(--color-text-disabled, #cbd5e1)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.375rem 0.5rem', textAlign: 'center' }}>
                      {row.__valid && inFileDuplicates.has(i)
                        ? <span title={t('students.importMap.duplicateInFile', 'Phone repeated in file — skipped')}><AlertCircle size={16} color="#ca8a04" /></span>
                        : row.__valid
                        ? <CheckCircle size={16} color="#059669" />
                        : <span title={t('students.importMap.missingName', 'Missing name')}><XCircle size={16} color="#dc2626" /></span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {built.maxRowsExceeded && (
            <p style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#ca8a04' }}>
              {t('students.importMap.maxRowsTrimmed', 'Only the first 500 rows will be imported.')}
            </p>
          )}
        </div>
      )}

      {/* STEP: Result */}
      {step === 'result' && importResult && (
        <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
          <CheckCircle size={48} style={{ color: '#059669', marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--color-text-primary)', marginBottom: '1rem' }}>
            {t('students.importMap.importComplete', 'Import complete')}
          </h3>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-success-light, #ecfdf5)', borderRadius: '12px', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#059669' }}>{importResult.created}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-success-dark, #065f46)' }}>{t('students.importMap.created', 'Created')}</div>
            </div>
            {importResult.updated > 0 && (
              <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-info-light, #eff6ff)', borderRadius: '12px', textAlign: 'center', minWidth: '90px' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2563eb' }}>{importResult.updated}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-info-dark, #1e40af)' }}>{t('students.importMap.updated', 'Updated')}</div>
              </div>
            )}
            {importResult.skipped > 0 && (
              <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-warning-light, #fffbeb)', borderRadius: '12px', textAlign: 'center', minWidth: '90px' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ca8a04' }}>{importResult.skipped}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-warning-dark, #92400e)' }}>{t('students.importMap.skipped', 'Skipped (duplicate phone)')}</div>
              </div>
            )}
            {importResult.notesCreated > 0 && (
              <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-info-light, #eff6ff)', borderRadius: '12px', textAlign: 'center', minWidth: '90px' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2563eb' }}>{importResult.notesCreated}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-info-dark, #1e40af)' }}>{t('students.importMap.notesLabel', 'Notes')}</div>
              </div>
            )}
            {importResult.failed > 0 && (
              <div style={{ padding: '1rem 1.5rem', backgroundColor: 'var(--color-danger-light, #fef2f2)', borderRadius: '12px', textAlign: 'center', minWidth: '90px' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>{importResult.failed}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-danger-dark, #991b1b)' }}>{t('students.importMap.failed', 'Failed')}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </EnterpriseModal>
  );
}
